/**
 * CreateGoalSheet Component
 *
 * BottomSheet with 2-step selection flow for creating a goal:
 * 1. SOP picker (radio-style scrollable list)
 * 2. Target level picker (pill buttons: Developing, Proficient, Exemplary)
 *
 * Handles empty SOP states (all SOPs have goals vs no zone assignment),
 * 409 Conflict (duplicate goal), and loading states.
 *
 * ARIA: SOP list uses role="radiogroup" + role="radio" + aria-checked.
 * Target pills use the same pattern (matching PreferencesSheet).
 */

import { useState, useEffect, useCallback } from 'react';
import { Trophy, Search } from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import { useCreateGoal } from '../hooks/useOperatorGoals';
import { getRubricLabel } from '../utils';
import { cn } from '../../../lib/utils';
import { ApiError } from '../../../lib/api';

interface CreateGoalSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** SOPs available for goal creation (already filtered to exclude SOPs with active goals) */
  availableSops: { id: string; title: string }[];
  /** Whether the operator has any existing goals (used to distinguish empty state causes) */
  hasExistingGoals: boolean;
  /** Pre-select this SOP when opening (for suggested goals) */
  defaultSopId?: string | null;
  /** Callback on successful creation */
  onCreated?: () => void;
}

// Target score options for the pill selector
const TARGET_OPTIONS = [
  { value: 60, label: 'Developing' },
  { value: 80, label: 'Proficient' },
  { value: 100, label: 'Exemplary' },
] as const;

export function CreateGoalSheet({
  isOpen,
  onClose,
  availableSops,
  hasExistingGoals,
  defaultSopId,
  onCreated,
}: CreateGoalSheetProps) {
  const [selectedSopId, setSelectedSopId] = useState<string | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<number>(80); // Default: Proficient
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createGoal = useCreateGoal();

  // Reset state when sheet opens/closes. Apply defaultSopId on open.
  useEffect(() => {
    if (isOpen) {
      setSelectedSopId(defaultSopId ?? null);
      setSelectedTarget(80);
      setErrorMessage(null);
    } else {
      setSelectedSopId(null);
      setSelectedTarget(80);
      setErrorMessage(null);
    }
  }, [isOpen, defaultSopId]);

  const handleCreate = useCallback(async () => {
    if (!selectedSopId) return;
    setErrorMessage(null);

    try {
      await createGoal.mutateAsync({
        sopId: selectedSopId,
        targetScore: selectedTarget as 0 | 20 | 40 | 60 | 80 | 100,
      });
      onCreated?.();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrorMessage('You already have an active goal for this SOP.');
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    }
  }, [selectedSopId, selectedTarget, createGoal, onCreated, onClose]);

  const canCreate = selectedSopId !== null && !createGoal.isPending;
  const isEmpty = availableSops.length === 0;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Set a New Goal"
      snapTo="full"
    >
      {isEmpty ? (
        <EmptyMessage hasExistingGoals={hasExistingGoals} />
      ) : (
        <div className="flex flex-col gap-6 pb-4">
          {/* SOP Picker */}
          <div>
            <p
              id="sop-picker-label"
              className="text-sm font-semibold text-op-text-primary"
            >
              Choose an SOP
            </p>
            <div
              role="radiogroup"
              aria-labelledby="sop-picker-label"
              className="mt-2 overflow-hidden rounded-xl bg-gray-50 ring-1 ring-black/[0.04]"
            >
              {availableSops.map((sop, i) => {
                const selected = selectedSopId === sop.id;
                return (
                  <button
                    key={sop.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={sop.title}
                    className={cn(
                      'flex w-full min-h-[48px] items-center gap-3 px-4 py-3 text-left transition-colors duration-150',
                      'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-guild-green',
                      selected ? 'bg-guild-green/5' : 'active:bg-gray-100',
                      i > 0 && 'border-t border-gray-100',
                    )}
                    onClick={() => setSelectedSopId(sop.id)}
                  >
                    {/* Radio dot */}
                    <span
                      className={cn(
                        'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-150',
                        selected
                          ? 'border-guild-green bg-guild-green'
                          : 'border-gray-300 bg-white',
                      )}
                    >
                      {selected && (
                        <span className="h-2 w-2 rounded-full bg-white" />
                      )}
                    </span>
                    {/* SOP title */}
                    <span
                      className={cn(
                        'text-sm line-clamp-1',
                        selected
                          ? 'font-semibold text-op-text-primary'
                          : 'font-medium text-op-text-secondary',
                      )}
                    >
                      {sop.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Target Level Picker */}
          <div>
            <p
              id="target-level-label"
              className="text-sm font-semibold text-op-text-primary"
            >
              Set your target
            </p>
            <div
              role="radiogroup"
              aria-labelledby="target-level-label"
              className="mt-2 flex flex-wrap gap-2"
            >
              {TARGET_OPTIONS.map((opt) => {
                const selected = selectedTarget === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={opt.label}
                    className={cn(
                      'min-h-[44px] whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150',
                      selected
                        ? 'bg-guild-green text-white'
                        : 'bg-gray-100 text-op-text-secondary active:bg-gray-200',
                      'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
                    )}
                    onClick={() => setSelectedTarget(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Error message */}
          {errorMessage && (
            <p className="text-sm text-red-600" role="alert">
              {errorMessage}
            </p>
          )}

          {/* Create button */}
          <button
            type="button"
            disabled={!canCreate}
            onClick={handleCreate}
            className={cn(
              'min-h-[48px] w-full rounded-xl text-sm font-semibold transition-colors duration-150',
              canCreate
                ? 'bg-guild-green text-white active:bg-guild-green/90'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed',
              'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
            )}
          >
            {createGoal.isPending ? 'Creating...' : `Create Goal \u2014 ${getRubricLabel(selectedTarget)}`}
          </button>
        </div>
      )}
    </BottomSheet>
  );
}

// ============================================
// EMPTY MESSAGE
// ============================================

function EmptyMessage({ hasExistingGoals }: { hasExistingGoals: boolean }) {
  if (hasExistingGoals) {
    // All focusSops already have active goals
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
          <Trophy size={32} strokeWidth={1.5} className="text-emerald-600" />
        </div>
        <p className="mt-4 text-base font-semibold text-op-text-primary">
          You have goals for all available SOPs
        </p>
        <p className="mt-1 text-sm text-op-text-secondary">
          Great job! Complete your current goals to unlock more.
        </p>
      </div>
    );
  }

  // No zone assignment yet — focusSops is empty
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
        <Search size={32} strokeWidth={1.5} className="text-gray-400" />
      </div>
      <p className="mt-4 text-base font-semibold text-op-text-primary">
        No SOPs available yet
      </p>
      <p className="mt-1 text-sm text-op-text-secondary">
        SOPs are assigned based on your zone. Check with your manager if you need access.
      </p>
    </div>
  );
}
